import { useCallback, useState } from "react";

import Link from "@whoppah/next/link";
import { useTranslation } from "next-i18next";
import { Locale, useRouter } from "next/router";

import { Currency } from "@/Apollo/schema";
import { SecurityOutlined } from "@/assets/icons/24/SecurityOutlined";
import { ProductUnknown } from "@/common/contracts/product.contracts";
import { analyticsService } from "@/common/services/analytics/analytics.service";
import { cn } from "@/common/utils/css.utils";
import { formatPrice } from "@/common/utils/intl.utils";
import {
  ALTERNATIVES_DISPLAY_COUNT,
  ALTERNATIVES_LEVEL,
  AlternativesLevel,
  CriterionKey,
  PriceAnchor,
  PriceBandOutcome,
  SoldCategoryFallback,
  buildCategoryFallbackPath,
  buildPriceAnchor,
} from "@/common/utils/sold-alternatives.utils";
import { ProductCard } from "@/features/search/components/product-card/ProductCard";
import { toCardProduct } from "@/features/search/utils/gateway-product.utils";
import { defaultLocale } from "@/i18n/config";
import { AnchorButton, AnchorButtonProps } from "@/ui/cva/Button";
import { Heading } from "@/ui/cva/Heading";
import { Body } from "@/ui/typography/Body";

/**
 * The comparable-alternatives module on a sold PDP.
 *
 * Two states, and no third:
 *
 *  - The ladder found something, so we show real product cards, using the
 *    same `ProductCard` the listing page and search results use. Nothing
 *    about a card is re-implemented here: price, sold/discount badges,
 *    labels and the favourite button all come from that component, so an
 *    alternative looks and behaves exactly like the same item does
 *    anywhere else on the site, and clicking it lands on its real PDP with
 *    its normal buy, shipping and delivery flow intact.
 *  - The ladder found nothing (L5). We deliberately do NOT render an empty
 *    module. The visitor gets a link to the item's own subcategory page
 *    with the brand filter already applied, which is an honest "here is
 *    where the rest of these live" instead of two unrelated items dressed
 *    up as comparable.
 *
 * `data-alternatives-level` stays on the wrapper alongside the analytics
 * event: the event answers "which rung serves real traffic" in aggregate,
 * the attribute lets a single page be inspected from a crawl or a console
 * without waiting for analytics to catch up.
 */

/**
 * "EUR 150 less than the piece you viewed", under one card.
 *
 * Split out so the null case is one place: `buildPriceAnchor` returns null
 * whenever the comparison would be invented (a missing price, mismatched
 * currencies, a difference too small to be a reason), and this renders
 * nothing rather than an empty line that would still cost the card height.
 *
 * Cheaper is the interesting case and gets the brand green. Dearer is
 * stated plainly in muted grey: a visitor who clicks through to find the
 * number for themselves loses more trust than the click was worth.
 */
const PriceAnchorLine = ({
  anchor,
  locale,
}: {
  anchor: PriceAnchor | null;
  locale: Locale;
}) => {
  const { t } = useTranslation();

  if (!anchor) {
    return null;
  }

  /*
    The amount goes in as a number with the currency in options, rather than
    as a Price object. `buildPriceAnchor` deliberately types the currency as
    a plain string, because `sold-alternatives.utils.ts` avoids the
    gitignored generated types so its rules stay unit-testable without a
    gateway token. The cast is where that meets `formatPrice`'s enum: the
    enum's values ARE these strings, so it is a type-level narrowing and not
    a runtime assumption.
  */
  const amount = formatPrice(anchor.amount, {
    locale,
    currency: (anchor.currency as Currency | null) ?? undefined,
  });

  return (
    <Body
      as="p"
      data-testid="sold-alternatives-price-anchor"
      data-anchor-direction={anchor.direction}
      className={cn(
        "mt-1 text-xs",
        anchor.direction === "less" ? "text-brand-500" : "text-grey-500"
      )}
    >
      {anchor.direction === "less"
        ? t("pdp:pdp-sold-anchor-less", {
            defaultValue: "{{amount}} less than the piece you viewed",
            amount,
          })
        : t("pdp:pdp-sold-anchor-more", {
            defaultValue: "{{amount}} more than the piece you viewed",
            amount,
          })}
    </Body>
  );
};

type SoldAlternativesProps = {
  alternatives: ProductUnknown[];
  level: AlternativesLevel;
  priceBand: PriceBandOutcome;
  matchCount: number;
  /**
   * The criteria the winning rung actually filtered on.
   *
   * Measurement only, never copy. It used to drive the subtitle ("Matched
   * on the same subcategory, brand and style"), which the design review
   * replaced with a line addressed to the reader. The provenance is still
   * worth having: it is what answers "which rung serves real traffic" from
   * a crawl or a console, so it stays on the module as a data attribute.
   */
  matchedCriteria?: CriterionKey[];
  categoryFallback: SoldCategoryFallback | null;
  /**
   * The sold item's ASKING price, which every card is anchored against.
   * Never its realised price: that is redacted out of this page.
   */
  soldAskingMoney?: { amount?: number | null; currency?: string | null } | null;
  /**
   * Where "see all N comparable X" points: the sold item's own subcategory
   * with its brand pre-applied, the same narrowed listing the L5 dead end
   * uses. Computed for every rung, not just L5.
   */
  browseAll?: SoldCategoryFallback | null;
  /** How many cards to render. */
  count?: number;
  /**
   * Renders the 2-vs-4 count toggle. Preview deployments only: this is a
   * decision aid for an internal review, not a shipped control.
   */
  showCountToggle?: boolean;
  /**
   * Treat the module as the page's hero rather than as a footnote under
   * the real content.
   *
   * On the sold page the alternatives ARE the page, so they carry the
   * page's largest heading and the grid takes the full measure. On any
   * other surface this module would sit below the item being viewed, where
   * an h4 would out-shout the thing it belongs to.
   */
  hero?: boolean;
  className?: string;
};

const COUNT_OPTIONS = [2, 4];

const LIST_NAME = "sold-pdp-alternatives";

/** How many card images are worth fetching before the browser asks. */
const EAGER_CARD_COUNT = 2;

export const SoldAlternatives = ({
  alternatives,
  level,
  priceBand,
  matchCount,
  matchedCriteria = [],
  categoryFallback,
  soldAskingMoney = null,
  browseAll = null,
  count = ALTERNATIVES_DISPLAY_COUNT,
  showCountToggle = false,
  hero = false,
  className,
}: SoldAlternativesProps) => {
  const { t } = useTranslation();
  const { locale } = useRouter();
  const [activeCount, setActiveCount] = useState(count);

  const shown = alternatives.slice(0, activeCount);

  const resolvedLocale = (locale ?? defaultLocale) as Locale;

  /*
    The trust sentence at the point of decision.

    The USP band below the fold is right for the person who scrolls. The
    person deciding between four cards never gets there, so the two claims
    that actually answer their fear go directly under the heading, in sight
    of the cards.

    Both strings are the live brand-promise copy, already translated into
    all six locales: "Designer items curated by Whoppah" and "We only pay
    the seller once you have safely received the item". Writing new copy
    here would have shipped English into five locales for the one line on
    the page whose whole job is being believed.
  */
  const trustLine = [
    t("pdp:pdp-brand-promise-usp-2-v2"),
    t("pdp:pdp-brand-promise-usp-3-v2"),
  ]
    .filter(Boolean)
    .join(" · ");

  /*
    Inventory depth. The ladder already counts the full match set and the
    page already carries it as `data-alternatives-matches`; until now it was
    computed and thrown away. A visitor who sees four cards has no reason to
    think 454 exist.

    Suppressed when the count does not exceed what is on screen, so the
    club chair (4 matches, 4 shown) says nothing rather than inviting
    someone to "see all 4" of the four they can already see.
  */
  const browseAllPath = buildCategoryFallbackPath(browseAll);
  const showDepthLink = !!browseAllPath && matchCount > shown.length;

  // One event per sold-PDP render, so we can count which rung actually
  // fires in production rather than reasoning about it from the rules.
  analyticsService.useExecAfterMount(
    useCallback(() => {
      analyticsService.trackSoldAlternativesShown(
        level,
        priceBand,
        matchCount,
        shown.length
      );
      // Deliberately keyed on the ladder outcome, not on `shown.length`
      // alone: flipping the preview toggle should not re-fire the event.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [level, priceBand, matchCount])
  );

  const dataAttributes = {
    "data-testid": "sold-alternatives",
    "data-alternatives-level": level,
    "data-alternatives-price-band": priceBand,
    "data-alternatives-matches": String(matchCount),
    "data-alternatives-criteria": matchedCriteria.join(","),
    "data-alternatives-shown": String(shown.length),
  };

  if (level === ALTERNATIVES_LEVEL.L5 || !alternatives.length) {
    const fallbackPath = buildCategoryFallbackPath(categoryFallback);

    if (!fallbackPath) {
      return null;
    }

    return (
      <div
        {...dataAttributes}
        className={cn("flex flex-col items-start space-y-3", className)}
      >
        <Heading as="h2" size={hero ? "h4" : "h5"}>
          {t("pdp:pdp-sold-no-alternatives-title", {
            defaultValue: "Nothing comparable is listed right now",
          })}
        </Heading>
        <Body as="p" color="muted">
          {t("pdp:pdp-sold-no-alternatives-body", {
            defaultValue:
              "Browse the rest of this subcategory to see what is available.",
          })}
        </Body>
        {/*
          Category routes come out of the gateway in Dutch, the same way
          the PDP breadcrumbs receive them, so the link is translated from
          `nl` into the active locale. `localizeHref` preserves the query
          string, so the brand filter survives that rewrite.
        */}
        <Link
          data-testid="sold-alternatives-category-link"
          href={fallbackPath}
          sourceLocale="nl"
          className="rounded-[200px] border border-grey-500 px-4 py-2 font-title text-sm font-semibold hover:bg-grey-50"
        >
          {t("pdp:pdp-sold-browse-category", {
            defaultValue: "Browse the category",
          })}
        </Link>
      </div>
    );
  }

  return (
    <div
      {...dataAttributes}
      className={cn(
        "flex flex-col",
        hero ? "space-y-1.5 md:space-y-3" : "space-y-4",
        className
      )}
    >
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div className="flex flex-col space-y-1">
          <Heading as="h2" size={hero ? "h4" : "h5"}>
            {/*
              A new key rather than the old `pdp-sold-alternatives-title`.
              That key already carries "Comparable pieces you can buy" in
              six locales; reusing it would leave five of them saying the
              old thing until Lokalise catches up, and English saying the
              new one.
            */}
            {t("pdp:pdp-sold-similar-title", {
              defaultValue: "Similar items",
            })}
          </Heading>
          {/*
            The subtext talks to the person reading it. It replaced
            "Matched on the same subcategory, material, condition and
            colour", which described the matching logic instead: true, and
            of no use to anyone deciding between four cards. The criteria
            the ladder actually fired on are still inspectable on the
            module as `data-alternatives-*`, which is where a measurement
            question belongs.
          */}
          <Body
            as="p"
            color="muted"
            data-testid="sold-alternatives-subtext"
            className="text-sm"
          >
            {t("pdp:pdp-sold-alternatives-subtext", {
              defaultValue:
                "But maybe you will like one of these just as much.",
            })}
          </Body>
        </div>

        {showCountToggle ? (
          <div
            data-testid="sold-alternatives-count-toggle"
            /*
              From `md` up only. On a narrow screen this wraps onto its own
              row and costs about 45px, which is the difference between the
              notify control being inside the fold at 375x812 and 14px
              below it. It is a decision aid for an internal review of how
              many cards to ship, that review happens on a desktop, and
              hiding it here means the mobile layout a reviewer measures on
              the preview is the same one a visitor gets in production
              rather than 45px taller.
            */
            className="hidden items-center gap-1 rounded-[200px] border border-grey-500 p-1 md:flex"
          >
            {COUNT_OPTIONS.map(option => (
              <button
                key={option}
                type="button"
                aria-pressed={activeCount === option}
                onClick={() => setActiveCount(option)}
                className={cn(
                  "rounded-[200px] px-3 py-1 font-title text-sm font-semibold",
                  activeCount === option
                    ? "bg-brand-500 text-white"
                    : "text-black hover:bg-grey-50"
                )}
              >
                {t("pdp:pdp-sold-alternatives-count-option", {
                  defaultValue: "Show {{count}}",
                  count: option,
                })}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      {/*
        Trust, in sight of the cards. Its own row rather than inside the
        heading column, so it takes the full measure instead of being
        squeezed to a third of it by the count toggle beside it.
      */}
      {trustLine ? (
        <Body
          as="p"
          data-testid="sold-alternatives-trust-line"
          className="flex items-start gap-1.5 text-xs text-grey-800 md:text-sm"
        >
          <SecurityOutlined className="size-4 shrink-0 translate-y-[0.1rem] text-brand-500" />
          <span>{trustLine}</span>
        </Body>
      ) : null}

      {/*
        Two layouts, decided by geometry rather than by taste.

        From `md` up this is the grid it looks like: two or four columns
        depending on the count.

        Below `md` it is a snap scroller, because at 375x812 a second row
        cannot be above the fold and the fold is the requirement. The
        arithmetic: 40px marquee plus 118px header is fixed site chrome,
        the sold strip is 116px and the notify control is 72px, which
        leaves about 426px for this module. One row of `ProductCard` is
        293px and a heading is about 70px, so one row fits and two rows
        (594px) cannot. Rather than drop to two alternatives on the device
        that carries most of the traffic, all four stay on the page and the
        third and fourth are a swipe away. `basis-[46%]` leaves the third
        card visibly peeking, so it is obvious there are more.

        If the honest answer turns out to be "show two on mobile", that is
        the `count` prop and nothing else.
      */}
      <div
        className={cn(
          hero
            ? "flex snap-x snap-mandatory gap-2 overflow-x-auto pb-1 md:grid md:snap-none md:gap-4 md:overflow-x-visible md:pb-0"
            : "grid grid-cols-2 gap-2 md:gap-4",
          activeCount > 2 ? "md:grid-cols-4" : "md:grid-cols-2"
        )}
      >
        {shown.map((alternative, index) => (
          <div
            key={alternative.id}
            className={cn(
              "min-w-0",
              hero && "shrink-0 basis-[46%] snap-start md:shrink md:basis-auto"
            )}
          >
            <ProductCard
              product={toCardProduct(alternative)}
              /*
                The first two cards load eagerly, the rest stay lazy.

                On this page the grid is the content, and on a phone the
                first two cards are the ones inside the fold. Without this
                every card image here was lazy and the section could paint
                as a row of empty frames before the browser got round to
                fetching anything. Two rather than four: on desktop all
                four are visible, but preloading four full-size card images
                to win the last two is a worse trade than letting them
                arrive a moment later.
              */
              priority={index < EAGER_CARD_COUNT}
              /*
                `trackSelectItem` returns early when `searchMeta` is nil
                (`analytics.service.ts`), so passing no search meta silently
                swallowed every click on an alternative. These results come
                from the ladder rather than from an Algolia query, so there
                is no queryID to attribute, but the page/limit pair is real
                and is what the other card lists pass.

                Analytics gets the gateway product, not the converted
                card one — its mapper reads the gateway's nested shape, and
                the full product is right here.
              */
              onSelect={() =>
                analyticsService.trackSelectItem(
                  index + 1,
                  LIST_NAME,
                  LIST_NAME,
                  alternative,
                  { page: 1, limit: shown.length }
                )
              }
            />

            <PriceAnchorLine
              anchor={buildPriceAnchor(soldAskingMoney, alternative)}
              locale={resolvedLocale}
            />
          </div>
        ))}
      </div>

      {showDepthLink ? (
        /*
          A button, not the underlined text link this used to be.

          "See all 13 comparable pieces in Table accessories" undersold the
          one action the whole page is built around, and it read as a
          footnote under the grid. Solid Brand is right here because it is
          the only button in the section, so there is nothing for it to
          clash with, and the copy is short enough to survive the longer
          translations without wrapping.

          Same `sourceLocale="nl"` treatment as the L5 link: category
          routes arrive from the gateway in Dutch and `localizeHref`
          translates the segments while preserving the brand query string.
        */
        <AnchorButton
          {...({
            as: Link,
            href: browseAllPath as string,
            sourceLocale: "nl",
          } as AnchorButtonProps)}
          data-testid="sold-alternatives-depth-link"
          color="brand"
          appearance="solid"
          className="self-center text-center"
        >
          {t("pdp:pdp-sold-see-more", { defaultValue: "See more" })}
        </AnchorButton>
      ) : null}
    </div>
  );
};

export default SoldAlternatives;
